/**
 * src/api/errorLogApi.ts — client for the ErrorLog Resource.
 *
 * POST /ErrorLog is anonymous-allowed so errors that fire before auth
 * resolves still reach the dashboard. The server prefers session user
 * identity; `customer_email` and `role` in the payload are best-effort
 * fallbacks used only when the caller isn't authenticated.
 */

import { getCollection, post, type Collection } from './harperClient';

export interface ErrorLogRecord {
  uuid: string;
  customer_id: string | null;
  customer_email: string | null;
  role: string | null;
  route: string;
  message: string;
  stack: string;
  source: string;
  line: number | null;
  column: number | null;
  ws_log: string;
  user_agent: string;
  created_at: string;
}

export interface CreateErrorLogPayload {
  customer_email: string | null;
  role: string | null;
  route: string;
  message: string;
  stack: string;
  source: string;
  line: number | null;
  column: number | null;
  ws_log: string;
  user_agent: string;
}

export async function createErrorLog(payload: CreateErrorLogPayload): Promise<string> {
  // Anonymous-allowed endpoint: fetch directly (bypasses harperClient's
  // auth-required short-circuit) so pre-auth errors still land.
  const res = await post<{ uuid: string }>('/ErrorLog', payload, undefined, { anonymous: true });
  return res.uuid;
}

export async function listErrorLogs(): Promise<ErrorLogRecord[]> {
  const res: Collection<ErrorLogRecord> = await getCollection<ErrorLogRecord>('/ErrorLog');
  const rows = res.data ?? [];
  return rows.slice().sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
}
